import { Link } from '@tanstack/react-router'
import { ArrowUpRight } from 'lucide-react'
import { RESOURCE_COPY } from '../content/resource-copy'
import { RESOURCE_TYPE_CONFIG, getResourcePath } from '../content/resources'
import type { Resource } from '../content/resources'
import { Card } from '@/components/ui/card'

type ResourceCardProps = {
  resource: Resource
  featured?: boolean
}

function formatPublishedDate(date: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${date}T00:00:00Z`))
}

export function ResourceCard({ resource, featured }: ResourceCardProps) {
  const href = getResourcePath(resource)
  const copy = RESOURCE_COPY[resource.language]
  const typeLabel = RESOURCE_TYPE_CONFIG[resource.language][resource.type].label

  return (
    <Card className="group h-full gap-0 overflow-hidden rounded-[1.4rem] border-border bg-card/85 py-0 shadow-none transition-colors hover:border-upgrade-cta/45 hover:bg-card">
      <Link
        to={href}
        className={
          featured
            ? 'grid h-full min-h-[24rem] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:grid-cols-[minmax(0,1.12fr)_minmax(20rem,0.88fr)]'
            : 'flex h-full flex-col focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
        }
      >
        <div
          className={
            featured
              ? 'relative min-h-64 overflow-hidden border-b border-border bg-muted lg:min-h-full lg:border-b-0 lg:border-r'
              : 'relative aspect-[16/10] overflow-hidden border-b border-border bg-muted'
          }
        >
          {resource.coverImage ? (
            <img
              src={resource.coverImage}
              alt={resource.coverImageAlt ?? ''}
              loading={featured ? 'eager' : 'lazy'}
              decoding="async"
              className="h-full w-full object-cover object-left-top transition-transform duration-500 group-hover:scale-[1.025]"
            />
          ) : (
            // No cover: the article's topic over the brand glow, filling the frame.
            <div className="absolute inset-0 flex items-center justify-center bg-[radial-gradient(circle_at_30%_20%,var(--marketing-glow-strong),transparent_60%),radial-gradient(circle_at_80%_90%,var(--marketing-glow-soft),transparent_55%)] p-8">
              <span
                className={
                  featured
                    ? 'text-center text-3xl font-semibold tracking-[-0.03em] text-foreground/80 sm:text-4xl'
                    : 'text-center text-xl font-semibold tracking-[-0.02em] text-muted-foreground'
                }
              >
                {resource.topics?.[0] ?? 'Laplace'}
              </span>
            </div>
          )}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-foreground/10 to-transparent" />
        </div>
        <div
          className={
            featured
              ? 'flex flex-col justify-center p-7 sm:p-9 lg:p-11'
              : 'flex flex-1 flex-col p-6 sm:p-7'
          }
        >
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-[11px] uppercase tracking-[0.15em] text-upgrade-cta">
            <span>{typeLabel}</span>
            {resource.readingTime ? (
              <>
                <span className="size-1 rounded-full bg-muted-foreground/40" />
                <span className="normal-case tracking-normal text-muted-foreground">
                  {resource.readingTime}
                </span>
              </>
            ) : null}
          </div>
          <h3
            className={
              featured
                ? 'mt-5 text-3xl font-display font-medium leading-tight tracking-[-0.01em] text-foreground sm:text-4xl'
                : 'mt-4 text-xl font-semibold leading-tight tracking-[-0.02em] text-foreground'
            }
          >
            {resource.title}
          </h3>
          <p
            className={
              featured
                ? 'mt-4 text-base leading-7 text-muted-foreground'
                : 'mt-3 text-sm leading-6 text-muted-foreground'
            }
          >
            {resource.description}
          </p>
          <div className="mt-auto flex items-center justify-between gap-4 pt-7 text-sm text-muted-foreground">
            <time dateTime={resource.publishedAt}>
              {formatPublishedDate(resource.publishedAt, copy.locale)}
            </time>
            <span className="inline-flex items-center gap-1.5 font-medium text-foreground transition-colors group-hover:text-upgrade-cta">
              {copy.readResource}
              <ArrowUpRight className="size-4" aria-hidden="true" />
            </span>
          </div>
        </div>
      </Link>
    </Card>
  )
}
